import {ReactNode, createContext, useContext, useMemo} from 'react';
import {useIntl} from 'react-intl';
import {captureMessage} from '@sentry/browser';
import {deepMerge} from '@sail/utils';
import {
  ContactFormEndpointRequest,
  Profile,
} from '@stripe-internal/conversations-shared-dependencies';
import type {Api} from './api/api';
import {
  createCSATSurvey,
  CreateCSATSurveyRequest,
  submitCSATRating,
  SubmitCSATRatingRequest,
} from './api/csat';
import {
  fetchFeedback,
  FetchFeedbackRequest,
  submitFeedback,
  SubmitFeedbackRequest,
} from './api/feedback';
import {
  cancelRequestACall,
  fetchRequestACallCodes,
  fetchVerificationCodes,
  inProgressRequestACall,
  inProgressRequestACallV2,
  requestACall,
  RequestACallRequest,
} from './api/request-a-call';
import {fetchProfile, FetchProfileRequest} from './api/profile';
import {submitContactForm} from './api/email';
import {supportFetch} from '../../helpers/supportFetch';
import {fetchEligibleChannels} from './api/channels';
import {fetchTopics} from './api/topics';
import {fetchConnectPlatformTransparency} from './api/connect-platform';
import {useProfile} from '../ProfileProvider';
import {abandonChat, AbandonChatRequest} from './api/chat';
import {ConfigContext} from '../ConfigProvider';

type fetchShape = typeof fetch;

export function createApi(
  fetch: fetchShape,
  {profile}: {profile?: Profile} = {},
): Api {
  const isLoggedOut = profile?.visitor.sessionType === 'anonymous';
  const isLinkSession = profile?.visitor.sessionType === 'link';

  return {
    fetch,
    fetchEligibleChannels: (entryPoint: string, audience: string) =>
      fetchEligibleChannels(fetch, entryPoint, audience),
    fetchTopics: (entryPoint: string) => fetchTopics(fetch, entryPoint),
    fetchConnectPlatformTransparency: () =>
      fetchConnectPlatformTransparency(fetch),

    fetchProfile: (request: FetchProfileRequest) =>
      fetchProfile(fetch, request),

    createCSATSurvey: (request: CreateCSATSurveyRequest) =>
      createCSATSurvey(fetch, request),
    submitCSATRating: (request: SubmitCSATRatingRequest) =>
      submitCSATRating(fetch, request),

    fetchFeedback: (request: FetchFeedbackRequest) =>
      fetchFeedback(fetch, request),
    submitFeedback: (request: SubmitFeedbackRequest) =>
      submitFeedback(fetch, request),

    submitContactForm: (body: ContactFormEndpointRequest) =>
      submitContactForm(fetch, body),

    inProgressRequestACall: (entryPoint: string, audience: string) => {
      if (isLoggedOut || isLinkSession) {
        return Promise.resolve({code: 200, data: {inProgress: false}});
      }

      return profile?.featureFlags?.racInProgressV2
        ? inProgressRequestACallV2(fetch, entryPoint)
        : inProgressRequestACall(fetch, entryPoint, audience);
    },
    inProgressRequestACallV2: (entryPoint: string) => {
      if (isLoggedOut || isLinkSession) {
        return Promise.resolve({code: 200, data: {inProgress: false}});
      }

      return inProgressRequestACallV2(fetch, entryPoint);
    },
    requestACall: (request: RequestACallRequest) =>
      requestACall(fetch, request),
    fetchVerificationCodes: (audience: string) =>
      fetchVerificationCodes(fetch, audience),
    fetchRequestACallCodes: (entryPoint: string) => {
      if (isLoggedOut || isLinkSession) {
        return Promise.resolve({type: 'success', pending: false});
      }

      return fetchRequestACallCodes(fetch, entryPoint);
    },
    cancelRequestACall: (entryPoint: string) =>
      cancelRequestACall(fetch, entryPoint),
    abandonChat: (request: AbandonChatRequest) => abandonChat(fetch, request),
  };
}

const ApiContext = createContext<Api>(createApi(supportFetch));

export function useApi(): Api {
  return useContext(ApiContext);
}

function authedOrAnonUrl(requiresAuth: boolean, url: string) {
  const apiMatcher = /^\/?api\/(anon\/)?(.*)$/;

  if (requiresAuth) {
    return url.replace(apiMatcher, '/api/$2');
  }

  return url.replace(apiMatcher, '/api/anon/$2');
}

export function authedOrAnonRequest(
  requiresAuth: boolean,
  input: RequestInfo | URL,
): RequestInfo | URL {
  if (typeof input === 'string') {
    return authedOrAnonUrl(requiresAuth, input);
  }

  if ('url' in input) {
    return {
      ...input,
      url: authedOrAnonUrl(requiresAuth, input.url),
    };
  }

  // this shouldn't ever happen, but if it ever did I'd want to know
  captureMessage(
    "ApiProvider expected an api endpoint url, but didn't find one",
    {level: 'warning'},
  );

  return input;
}

function urlWithReferrerLocale(url: string, locale?: string): string {
  if (!locale) {
    return url;
  }
  const parsedUrl = new URL(url, window.location.origin);
  parsedUrl.searchParams.set('referrerLocale', locale);
  if (url.startsWith('/')) {
    return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
  }
  return parsedUrl.toString();
}

export function ApiProvider({
  children,
  fetch = supportFetch,
}: {
  children: ReactNode;
  fetch?: fetchShape;
}) {
  const config = useContext(ConfigContext);

  const {locale} = useIntl();
  const {profile} = useProfile();
  const {featureFlags} = profile;
  const {livemode} = profile;
  const {enableAnonSupportConversationsEndpoints: enableAnonEndpoints} =
    featureFlags;

  const requiresAuth = profile.visitor.sessionType !== 'anonymous';

  const apiFetch = useMemo(() => {
    const wrapped: fetchShape = async (input, init?) => {
      const inputWithAnonRoutes = enableAnonEndpoints
        ? authedOrAnonRequest(requiresAuth, input)
        : input;

      // stripe-livemode is used by manage
      const livemodeHeaders =
        livemode != null
          ? {'stripe-livemode': livemode ? 'true' : 'false'}
          : {};

      const localeHeaders = {
        'Accept-Language': [locale, ...navigator.languages],
      };

      const headers = {
        ...livemodeHeaders,
        ...localeHeaders,
      };

      if (config.entrypoint === 'dashboard' && profile.merchant?.id) {
        Object.assign(headers, {'Stripe-Account': profile.merchant.id});
      }

      // @ts-expect-error - TS2322 - Type 'string[]' is not assignable to type 'string'
      const mergedInit = deepMerge<RequestInit>({headers}, init);

      const serviceAwareInput =
        config.entrypoint === 'dashboard'
          ? `/conversations${inputWithAnonRoutes}`
          : inputWithAnonRoutes;

      const inputWithReferrerLocale =
        typeof serviceAwareInput === 'string'
          ? urlWithReferrerLocale(serviceAwareInput, locale)
          : serviceAwareInput;
      return fetch(inputWithReferrerLocale, mergedInit);
    };

    return wrapped;
  }, [
    enableAnonEndpoints,
    fetch,
    requiresAuth,
    livemode,
    locale,
    profile.merchant?.id,
    config.entrypoint,
  ]);

  const api = createApi(apiFetch, {profile});
  return <ApiContext.Provider value={api}>{children}</ApiContext.Provider>;
}
